import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { admin, type AdminNode } from '../../api'
import { formatBytes, formatDate, parseBytes } from '../../format'

export default function AdminNodes() {
  const queryClient = useQueryClient()
  const nodes = useQuery({ queryKey: ['admin', 'nodes'], queryFn: admin.nodes, refetchInterval: 10_000 })

  const [name, setName] = useState('')
  const [quota, setQuota] = useState('')
  const [error, setError] = useState<string | null>(null)
  /** The enrollment token is only ever shown once, right after it is minted. */
  const [issued, setIssued] = useState<{ name: string; token: string } | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] })

  const create = useMutation({
    mutationFn: () => {
      const quotaBytes = quota.trim() === '' ? null : parseBytes(quota)
      if (quota.trim() !== '' && quotaBytes === null) {
        throw new Error('용량은 "500GB" 같은 형식으로 입력하세요')
      }
      return admin.createNode({ name: name.trim(), quotaBytes })
    },
    onSuccess: (result) => {
      setIssued({ name: result.node.name, token: result.token })
      setName('')
      setQuota('')
      setError(null)
      void invalidate()
    },
    onError: (err: Error) => setError(err.message),
  })

  const rotate = useMutation({
    mutationFn: (node: AdminNode) =>
      admin.rotateNodeToken(node.id).then((r) => ({ name: node.name, token: r.token })),
    onSuccess: (result) => {
      setIssued(result)
      void invalidate()
    },
    onError: (err: Error) => setError(err.message),
  })

  const scan = useMutation({
    mutationFn: (id: string) => admin.scanNode(id),
    onSuccess: invalidate,
    onError: (err: Error) => setError(err.message),
  })

  const remove = useMutation({
    mutationFn: (id: string) => admin.deleteNode(id),
    onSuccess: invalidate,
    onError: (err: Error) => setError(err.message),
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    create.mutate()
  }

  return (
    <section>
      <form className="row-form" onSubmit={submit}>
        <input
          placeholder="노드 이름 (예: 집 NAS)"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
        <input
          placeholder="용량 제한 (비우면 무제한)"
          value={quota}
          onChange={(event) => setQuota(event.target.value)}
        />
        <button type="submit" className="primary">
          노드 추가
        </button>
      </form>

      {error && (
        <p className="error banner" onClick={() => setError(null)}>
          {error}
        </p>
      )}

      {issued && <EnrollmentCard name={issued.name} token={issued.token} onClose={() => setIssued(null)} />}

      <table className="entries">
        <thead>
          <tr>
            <th>이름</th>
            <th>상태</th>
            <th className="num">사용량</th>
            <th className="num">디스크 여유</th>
            <th>마운트</th>
            <th className="num">마지막 확인</th>
            <th className="actions" />
          </tr>
        </thead>
        <tbody>
          {nodes.data?.length === 0 && (
            <tr>
              <td colSpan={7} className="muted center">
                노드가 없습니다. 위에서 추가하세요.
              </td>
            </tr>
          )}
          {nodes.data?.map((node) => (
            <tr key={node.id}>
              <td>{node.name}</td>
              <td>
                <span className={`dot ${node.online ? 'online' : 'offline'}`} />
                {node.online ? '온라인' : '오프라인'}
              </td>
              <td className="num">
                {formatBytes(node.usedBytes)}
                {node.quotaBytes !== null && ` / ${formatBytes(node.quotaBytes)}`}
              </td>
              <td className="num">
                {node.info?.freeBytes != null ? formatBytes(node.info.freeBytes) : '-'}
                {/* A node split across disks has no single meaningful figure. */}
                {(node.info?.filesystems.length ?? 0) > 1 && (
                  <>
                    <br />
                    <span className="muted small">디스크 {node.info?.filesystems.length}개 합계</span>
                  </>
                )}
              </td>
              <td className="mono small">
                {node.info?.root ?? '-'}
                {(node.info?.filesystems.length ?? 0) > 1 &&
                  node.info?.filesystems.map((fs) => (
                    <div key={fs.path} className="muted">
                      {fs.path} · {formatBytes(fs.freeBytes)} 여유
                    </div>
                  ))}
              </td>
              <td className="num small">{formatDate(node.lastSeenAt)}</td>
              <td className="actions">
                <button
                  type="button"
                  className="link"
                  disabled={!node.online || scan.isPending}
                  onClick={() => scan.mutate(node.id)}
                >
                  사용량 재측정
                </button>
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    if (!window.confirm(`${node.name}의 토큰을 재발급할까요? 실행 중인 에이전트는 연결이 끊깁니다.`)) return
                    rotate.mutate(node)
                  }}
                >
                  토큰 재발급
                </button>
                <button
                  type="button"
                  className="link danger"
                  onClick={() => {
                    if (!window.confirm(`${node.name}을 삭제할까요? 공유 설정도 함께 사라집니다. 실제 파일은 그대로 남습니다.`)) return
                    remove.mutate(node.id)
                  }}
                >
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

function EnrollmentCard({
  name,
  token,
  onClose,
}: {
  name: string
  token: string
  onClose: () => void
}) {
  // The image and the hub's public address are deployment facts the browser
  // cannot infer, so the server supplies them.
  const install = useQuery({ queryKey: ['admin', 'agent-install'], queryFn: admin.agentInstall })

  const command = [
    'docker run -d --restart=always \\',
    '  --name arthur-agent \\',
    // Without this on Linux the agent runs as root and every uploaded file
    // ends up owned by root on the host.
    '  --user $(id -u):$(id -g) \\',
    `  -e HUB_URL=${install.data?.hubUrl ?? '…'} \\`,
    `  -e NODE_TOKEN=${token} \\`,
    '  -v /경로/를/여기에:/data \\',
    `  ${install.data?.image ?? '…'}`,
  ].join('\n')

  return (
    <div className="callout">
      <div className="callout-head">
        <strong>{name} 설치 명령</strong>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>
      <p className="muted small">
        토큰은 지금만 볼 수 있습니다. 이 명령을 파일을 두고 싶은 서버에서 실행하세요.
        <code>-v</code> 의 왼쪽 경로만 실제 폴더로 바꾸면 됩니다.
      </p>
      <pre className="mono">{command}</pre>
      <button
        type="button"
        disabled={!install.data}
        onClick={() => void navigator.clipboard.writeText(command)}
      >
        명령 복사
      </button>
      {install.data?.image === 'arthur-storage/agent' && (
        <p className="notice small">
          이 이미지 이름은 직접 빌드한 서버에만 있습니다. 레지스트리에 올린 이미지를 쓰려면 허브의{' '}
          <code>AGENT_IMAGE</code> 환경변수를 설정하세요.
        </p>
      )}
    </div>
  )
}
